import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useFormikContext } from 'formik';

// components
import LoadingSpinner from '../../../../../components/loading/nestedSpinner';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../../../constants/aampeTheme';
import { FormulaTestMessageType } from '../../../../../constants/formula';
import { FormulaType } from 'constants/formulaType';

//context
import { EditorContext } from '../../../../../context/EditorContext';

import { createShouldForwardProp } from '../../../../../helpers/shouldForwardProp';

// hooks
import useNotifyDeveloper from '../../../../../hooks/common/useNotifyDeveloper';
import useSendFormulaTestMessageMutation from '../../../../../hooks/formulas/useSendFormulaTestMessage';
import useHandleQueryParams from '../../../../../hooks/helper/useHandleQueryParams';
import usePushIntegrationsQuery from '../../../../../hooks/pushIntegrations/usePushIntegrationsQuery';

// models
import { FormulaTestMessageFormValues } from '../../../../../models/formula.model';
import { NotifyDeveloperRequest } from '../../../../../models/notify-developer.model';
import { PushIntegrationModel } from '../../../../../models/pushProvider.model';

import TestFormulaError from '../../sections/TestFormulaError';
import TestFormulaNotify from '../../sections/TestFormulaNotify';
import TestFormulaSuccess from '../../sections/TestFormulaSuccess';
import TestPushProvider from '../../sections/TestPushProvider';
import TestFormulaContainer from './TestFormulaContainer';
import { TestFormulaSectionType } from './constant';

const test_message_id = 'test_message_id';
const shouldForwardProps = createShouldForwardProp(['isModalView']);

const TestFormulaV1: React.FC<
    React.PropsWithChildren<{
        deeplink: string;
        className?: string;
        handleClose?: () => void;
        isModalView?: boolean;
        formulaType?: FormulaType;
        currentTab?: string;
    }>
> = ({
    deeplink,
    className,
    handleClose,
    isModalView = false,
    formulaType,
    currentTab,
}) => {
    const { values, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();
    const {
        editorDetails: { formulaType: contextFormulaType },
    } = useContext(EditorContext);

    const { formulaId: formulaIdParam } = useParams<{ formulaId: string }>();
    const [queryParams] = useHandleQueryParams();

    const formulaId = formulaIdParam ?? queryParams.activeFormulaId;

    const { data, isPending: isLoading } = usePushIntegrationsQuery();

    const providedFormulaType = formulaType ? formulaType : contextFormulaType;
    const providerData = useMemo<PushIntegrationModel | undefined>(
        () =>
            data?.find((item) => item.channels?.includes(providedFormulaType!)),
        [data, providedFormulaType],
    );
    const testFormula = useSendFormulaTestMessageMutation(+formulaId);

    const notify = useNotifyDeveloper();

    const handleNotify = () => {
        notify.mutate(
            {
                request_code: NotifyDeveloperRequest.TestMessageNotReceived,
                metadata: {
                    contact_id: values.contact_ids,
                },
            },
            {
                onSuccess: () =>
                    setFieldValue(
                        'currentSection',
                        TestFormulaSectionType.NOTIFY_US,
                    ),
            },
        );
    };

    const handleNeedHelp = () => {
        notify.mutate(
            {
                request_code: NotifyDeveloperRequest.TestMessageNeedHelp,
            },
            {
                onSuccess: () =>
                    setFieldValue(
                        'currentSection',
                        TestFormulaSectionType.NEED_HELP,
                    ),
            },
        );
    };

    const handleReset = () => {
        setFieldValue('contact_ids', []);
        setFieldValue('testMessageContent', null);
        setFieldValue('selectedToken', null);
        setFieldValue('requestId', undefined);
        setFieldValue('currentSection', TestFormulaSectionType.FORM);
    };

    return (
        <Container
            isModalView={isModalView}
            className={className}
            id={test_message_id}
        >
            <Body isModalView={isModalView}>
                {isLoading && <LoadingSpinner />}
                {!isLoading && !providerData && <TestPushProvider />}
                {TestFormulaSectionType.FORM === values.currentSection &&
                    providerData && (
                        <TestFormulaContainer
                            isLoading={testFormula.isPending}
                            handleNeedHelp={handleNeedHelp}
                            menuPortalId={test_message_id}
                            handleClose={handleClose}
                            isModalView={isModalView}
                            currentTab={currentTab}
                        />
                    )}
                {TestFormulaSectionType.SUCCESS === values.currentSection && (
                    <TestFormulaSuccess
                        providerId={providerData?.id}
                        testMessageContent={values.testMessageContent}
                        deeplink={deeplink}
                        contactId={values.contact_ids}
                        handleNotify={handleNotify}
                        selectedToken={values?.notification_token}
                        reset={handleReset}
                        requestId={values.requestId}
                        groupId={
                            values.current_state ===
                            FormulaTestMessageType.GroupUser
                                ? String(values.notification_token?.value)
                                : undefined
                        }
                    />
                )}
                {TestFormulaSectionType.ERROR === values.currentSection && (
                    <TestFormulaError contactId={values.contact_ids[0]} />
                )}
                {TestFormulaSectionType.NOTIFY_US === values.currentSection && (
                    <TestFormulaNotify
                        message={
                            'An email has been sent to the the team for review. We will get back at you shortly.'
                        }
                    />
                )}
                {TestFormulaSectionType.NEED_HELP === values.currentSection && (
                    <TestFormulaNotify
                        message={
                            'An email has been sent to the the team. The team will contact you to help.'
                        }
                    />
                )}
            </Body>
        </Container>
    );
};

const Container = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{
    isModalView: boolean;
}>(({ isModalView }) => ({
    width: '100%',
    height: isModalView ? undefined : '600px',
    marginTop: isModalView ? undefined : aampeTheme.spacing(2),
    border: isModalView
        ? undefined
        : `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: isModalView ? undefined : aampeTheme.borderRadius.default,
    overflow: 'auto',
}));

const Body = styled.div.withConfig({ shouldForwardProp: shouldForwardProps })<{
    isModalView: boolean;
}>(({ isModalView }) => ({
    ...(isModalView
        ? {}
        : {
              marginTop: aampeTheme.spacing(2),
              padding: aampeTheme.spacing(2),
          }),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: aampeTheme.spacing(2),
    overflow: 'auto',
    height: '100%',
}));

export default TestFormulaV1;
